import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
  NgZone,
  OnDestroy,
  ChangeDetectorRef,
  Optional
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

type WindowPosition = 'screenCenter' | 'mouseCenter'
@Component({
  selector: 'nrk-window',
  templateUrl: './nrk-window.component.html',
  styleUrls: ['./nrk-window.component.scss']
})
export class NrkWindowComponent implements AfterViewInit, OnDestroy {

  @Input() header = true;
  @Input() initialShow = false;
  @Input() style: any = null;
  @Input() data: any = null;
  @Input() windowPosition: WindowPosition = 'screenCenter';
  @Input() caller: ElementRef | HTMLElement | null = null;

  @Output() endResize = new EventEmitter<ElementRef>();
  @Output() resize = new EventEmitter<ElementRef>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  // @ViewChild('headerEl', { static: true }) headerEl!: ElementRef;
  // @ViewChild('window', { static: true }) windowEl!: ElementRef;
  private windowEl!: ElementRef;
  @ViewChild('window')
  set windowSetter(el: ElementRef | undefined) {
    this.windowEl = el;
    this.tryInitialize();
  }

  private headerEl!: ElementRef;
  @ViewChild('headerEl')
  set headerSetter(el: ElementRef | undefined) {
    this.headerEl = el;
    this.tryInitialize();
  }


  private destroy$ = new Subject<void>();

  private offsetX = 0;
  private offsetY = 0;
  private mouseX = 0;
  private mouseY = 0;
  private isDragging = false;

  isVisible = false;
  private isInitialized = false;

  private routerSub: Subscription | null = null;
  private observer: MutationObserver | null = null;

  constructor(
    private renderer: Renderer2,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    @Optional() private router?: Router
  ) {}

  ngAfterViewInit(): void {
    // this.initDrag();

    if (this.initialShow) {
      this.show();
    }
  }

  private initDrag(): void {
    this.zone.runOutsideAngular(() => {
      const mouseDown$ = fromEvent<MouseEvent>(
        this.headerEl.nativeElement,
        'mousedown'
      );
      const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove');
      const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

      console.log('init drag', mouseDown$, mouseMove$, mouseUp$);

      mouseDown$
        .pipe(
          switchMap((startEvent) => {
            this.isDragging = true;
            document.body.classList.add('no-select');

            const rect = this.windowEl.nativeElement.getBoundingClientRect();
            this.offsetX = startEvent.clientX - rect.left;
            this.offsetY = startEvent.clientY - rect.top;

            return mouseMove$.pipe(
              tap((moveEvent) => {
                const newX = moveEvent.clientX - this.offsetX;
                const newY = moveEvent.clientY - this.offsetY;

                console.log('move event', newX, newY);

                this.renderer.setStyle(
                  this.windowEl.nativeElement,
                  'left',
                  newX + 'px'
                );
                this.renderer.setStyle(
                  this.windowEl.nativeElement,
                  'top',
                  newY + 'px'
                );
              }),
              takeUntil(mouseUp$)
            );
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();

      mouseUp$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        console.log('mnouse up event');
        this.isDragging = false;
        document.body.classList.remove('no-select');
        this.zone.run(() => this.endResize.emit(this.windowEl));
      });
    });
  }

  get isOpen(): boolean {
    return this.isVisible;
  }

  open(data: any = null, mouseEvent: MouseEvent = null): void {
    if (this.windowPosition === 'mouseCenter' && !mouseEvent) {
      console.error('Can\'t use windowPosition with mouseCenter if the mouse event is not passed!');
    }
    if (mouseEvent) {
      this.mouseX = mouseEvent.clientX;
      this.mouseY = mouseEvent.clientY;
    }
    this.data = data;
    this.show();
  }

  close(): void {
    this.hide();
  }

  show(): void {
    this.isVisible = true;
    setTimeout(() => {
      this.tryInitialize();
    });
    this.onOpen.emit();
  }

  private tryInitialize(): void {
    if (
      this.isVisible &&
      this.windowEl &&
      this.headerEl
    ) {
      this.initializeWindow();
    }
  }

  private setupAutoClose(): void {
    if (this.router && !this.routerSub) {
      this.routerSub = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => this.hide());
    }

    const callerEl = this.caller instanceof ElementRef
      ? (this.caller as ElementRef).nativeElement
      : this.caller as HTMLElement | null;

    if (callerEl && !this.observer) {
      const parent = callerEl.parentElement;
      if (parent) {
        this.observer = new MutationObserver(() => {
          if (!document.body.contains(callerEl)) {
            this.hide();
            this.observer?.disconnect();
            this.observer = null;
          }
        });
        this.observer.observe(parent, { childList: true });
      }
    }
  }

  private initializeWindow(): void {

    document.body.appendChild(this.windowEl.nativeElement);

    const windowRect = this.windowEl.nativeElement.getBoundingClientRect();

    let left = (window.innerWidth - windowRect.width) / 2;
    let top = (window.innerHeight - windowRect.height) / 2;

    if (this.windowPosition === 'mouseCenter') {
      left = this.mouseX - windowRect.width / 2;
      top = this.mouseY - windowRect.height / 2;
    }

    this.renderer.setStyle(
      this.windowEl.nativeElement,
      'transform',
      'unset'
    );
    this.renderer.setStyle(
      this.windowEl.nativeElement,
      'left',
      left + 'px'
    );
    this.renderer.setStyle(
      this.windowEl.nativeElement,
      'top',
      top + 'px'
    );

    this.initDrag();
    this.setupAutoClose();

  }

  // show(): void {
  //   // this.renderer.setStyle(this.windowEl.nativeElement, 'display', 'flex');
  //   this.isVisible = true;

  //   // ✅ Original centering calculation
  //   if (!this.isInitialized) {
  //     this.initDrag();
  //     const windowRect = this.windowEl.nativeElement.getBoundingClientRect();
  //     const left = (window.innerWidth - windowRect.width) / 2;
  //     const top = (window.innerHeight - windowRect.height) / 2;

  //     this.renderer.setStyle(this.windowEl.nativeElement, 'transform', 'unset');
  //     this.renderer.setStyle(this.windowEl.nativeElement, 'left', left + 'px');
  //     this.renderer.setStyle(this.windowEl.nativeElement, 'top', top + 'px');

  //     this.isInitialized = true;
  //   }

  //   // this.isVisible = true;
  //   this.onOpen.emit();
  // }

  hide(): void {
    // this.renderer.setStyle(this.windowEl.nativeElement, 'display', 'none');
    console.log('hiding window');
    this.isVisible = false;
    this.cdr.detectChanges();
    this.onClose.emit();
  }

  toggle(): void {
    this.isVisible ? this.hide() : this.show();
  }

  ngOnDestroy(): void {
    if (this.windowEl?.nativeElement?.parentElement) {
      this.renderer.removeChild(
        this.windowEl.nativeElement.parentElement,
        this.windowEl.nativeElement
      );
    }
    this.isVisible = false;
    this.destroy$.next();
    this.destroy$.complete();
    this.routerSub?.unsubscribe();
    this.observer?.disconnect();
  }
}

// import {
//   AfterViewInit,
//   Component,
//   ElementRef,
//   EventEmitter,
//   Input,
//   Output,
//   Renderer2,
//   ViewChild,
//   NgZone,
//   OnDestroy
// } from '@angular/core';
// import { fromEvent, Subject } from 'rxjs';
// import { switchMap, takeUntil, tap } from 'rxjs/operators';

// @Component({
//   selector: 'nrk-window',
//   templateUrl: './nrk-window.component.html',
//   styleUrls: ['./nrk-window.component.scss']
// })
// export class NrkWindowComponent implements AfterViewInit, OnDestroy {

//   @Input() header = true;
//   @Input() initialShow = false;
//   @Input() style = null;
//   @Input() data: any = null;

//   @Output() endResize = new EventEmitter<ElementRef>();
//   @Output() resize = new EventEmitter<ElementRef>();
//   @Output() onOpen = new EventEmitter<void>();
//   @Output() onClose = new EventEmitter<void>();

//   @ViewChild('window', { static: true }) windowEl!: ElementRef;
//   @ViewChild('headerEl', { static: true }) headerEl!: ElementRef;

//   private destroy$ = new Subject<void>();

//   private currentX = 0;
//   private currentY = 0;

//   private hasInitializedDrag = false;

//   isVisible = false;

//   constructor(
//     private renderer: Renderer2,
//     private zone: NgZone
//   ) {}

//   ngAfterViewInit(): void {
//     this.initDrag();

//     if (this.initialShow) {
//       this.show();
//     }
//   }

//   // ✅ Run drag fully outside Angular
//   private initDrag(): void {
//     this.zone.runOutsideAngular(() => {
//       // const mouseDown$ = fromEvent<MouseEvent>(
//       //   this.windowEl.nativeElement,
//       //   'mousedown'
//       // );
//       const mouseDown$ = fromEvent<MouseEvent>(
//         this.headerEl.nativeElement,
//         'mousedown'
//       );

//       const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove');
//       const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

//       mouseDown$
//         .pipe(
//           switchMap((startEvent) => {

//             // 🔹 On first drag, sync JS position with actual window position
//             // if (!this.hasInitializedDrag) {
//             //   const rect = this.windowEl.nativeElement.getBoundingClientRect();

//             //   this.currentX = rect.left;
//             //   this.currentY = rect.top;

//             //   // Remove CSS transform centering
//             //   this.renderer.setStyle(this.windowEl.nativeElement, 'transform', 'translate3d(0, 0, 0)');

//             //   this.hasInitializedDrag = true;
//             // }
//             if (!this.hasInitializedDrag) {
//               const rect = this.windowEl.nativeElement.getBoundingClientRect();
//               this.currentX = rect.left;
//               this.currentY = rect.top;

//               // Smooth transition for first transform override
//               this.renderer.setStyle(this.windowEl.nativeElement, 'transition', 'transform 0s');
//               this.renderer.setStyle(this.windowEl.nativeElement, 'transform', `translate3d(${this.currentX}px, ${this.currentY}px, 0)`);

//               this.hasInitializedDrag = true;
//             }

//             const startX = startEvent.clientX;
//             const startY = startEvent.clientY;

//             const originX = this.currentX;
//             const originY = this.currentY;

//             document.body.classList.add('no-select');

//             return mouseMove$.pipe(
//               tap((moveEvent) => {
//                 const dx = moveEvent.clientX - startX;
//                 const dy = moveEvent.clientY - startY;

//                 this.currentX = originX + dx;
//                 this.currentY = originY + dy;

//                 this.renderer.setStyle(
//                   this.windowEl.nativeElement,
//                   'transform',
//                   `translate3d(${this.currentX}px, ${this.currentY}px, 0)`
//                 );
//               }),
//               takeUntil(mouseUp$)
//             );
//           }),
//           takeUntil(this.destroy$)
//         )
//         .subscribe();

//       mouseUp$
//         .pipe(takeUntil(this.destroy$))
//         .subscribe(() => {
//           document.body.classList.remove('no-select');

//           // If you need Angular to know final position:
//           this.zone.run(() => {
//             this.endResize.emit(this.windowEl);
//           });
//         });
//     });
//   }

//   // ✅ No layout getter
//   get isOpen(): boolean {
//     return this.isVisible;
//   }


//   open(data: any = null): void {
//     this.data = data;
//     this.show();
//   }

//   close(): void {
//     this.hide();
//   }

//   show(): void {
//     this.renderer.setStyle(
//       this.windowEl.nativeElement,
//       'display',
//       'flex'
//     );

//     this.isVisible = true;
//     this.onOpen.emit();
//   }

//   hide(): void {
//     this.renderer.setStyle(
//       this.windowEl.nativeElement,
//       'display',
//       'none'
//     );

//     this.isVisible = false;
//     this.onClose.emit();
//   }

//   toggle(): void {
//     this.isVisible ? this.hide() : this.show();
//   }

//   ngOnDestroy(): void {
//     this.destroy$.next();
//     this.destroy$.complete();
//   }
// }








// import { AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';

// @Component({
//   selector: 'nrk-window',
//   templateUrl: './nrk-window.component.html',
//   styleUrls: ['./nrk-window.component.scss']
// })
// export class NrkWindowComponent implements OnInit, AfterViewChecked {

//   @Input() header = true;
//   @Input() initialShow = false;
//   @Input() style = null;
//   @Input() data: any = null;
//   @Output() endResize = new EventEmitter<ElementRef>()
//   @Output() resize = new EventEmitter<ElementRef>()
//   @Output() onOpen = new EventEmitter<ElementRef>();
//   @Output() onClose = new EventEmitter<void>();
//   @ViewChild('windowElement', { static: true }) windowElement: ElementRef;
//   @ViewChild('window', { static: true }) windowEl: ElementRef;
//   @ViewChild('windowContent', { static: true }) windowContent: ElementRef;

//   public get isOpen() {
//     const display = window.getComputedStyle(this.windowEl?.nativeElement)?.getPropertyValue('display');
//     return display && display !== 'none';
//   }

//   private isInitialized = false;
//   private isResizing = false;
//   private resizeHandle: string;
//   private isDragging = false;
//   private initialX = 0;
//   private initialY = 0;
//   private offsetX = 0;
//   private offsetY = 0;

//   resizeObserver: ResizeObserver;

//   constructor(private elRef: ElementRef, private renderer: Renderer2) { }

//   ngAfterViewChecked(): void {

//     if (this.isOpen && !this.isInitialized) {
//       const windowRect = this.windowEl.nativeElement.getBoundingClientRect();
//       const left = (window.innerWidth - windowRect.width) / 2;
//       const top = (window.innerHeight - windowRect.height) / 2;
//       this.renderer.setStyle(this.windowEl.nativeElement, 'transform', 'unset');
//       this.renderer.setStyle(this.windowEl.nativeElement, 'left', left + 'px');
//       this.renderer.setStyle(this.windowEl.nativeElement, 'top', top + 'px');
//     }
//   }

//   ngOnInit(): void {

//     if (this.initialShow) {
//       this.show();
//     }
    
//     this.resizeObserver = new ResizeObserver(entries => {
//       for (const entry of entries) {
//         if (entry.target === this.windowEl.nativeElement) {
//           this.isResizing = true;
//           break;
//         }
//       }
//     });

//     this.resizeObserver.observe(this.windowEl.nativeElement);
//     this.isResizing = false;
//   }

//   @HostListener('document:mousemove', ['$event'])
//   onMouseMove(event: MouseEvent) {

//     // if (this.isResizing) {
//     //   this.resizeWindow(event);
//     // } else if (this.isDragging) {
//     //   this.dragWindow(event);
//     // }
//     if (this.isDragging) {
//       this.dragWindow(event);
//     }
//   }

//   @HostListener('document:mouseup')
//   onMouseUp() {

//     if (this.isResizing) {
//       this.endResize.emit(this.windowContent);
//     }

//     this.isResizing = false;
//     this.resizeHandle = '';

//     this.isDragging = false;
//     this.renderer.removeClass(document.body, 'no-select');
//   }

//   // startResize(event: MouseEvent, handle: string) {
//   //   this.isResizing = true;
//   //   this.resizeHandle = handle;
//   //   event.preventDefault();
//   // }

//   startDrag(event: MouseEvent): void {
//     this.isDragging = true;
//     this.isInitialized = true;

//     const windowRect = this.windowEl.nativeElement.getBoundingClientRect();
//     this.initialX = windowRect.left;
//     this.initialY = windowRect.top;
//     this.offsetX = event.clientX;
//     this.offsetY = event.clientY;

//     this.renderer.addClass(document.body, 'no-select'); // Prevent text selection during drag
//   }


//   // private resizeWindow(event: MouseEvent) {
//   //   if (this.resizeHandle === 'top') {
//   //     // Resize from the top
//   //     const newHeight = Math.max(100, this.windowElement.nativeElement.offsetHeight - event.movementY);
//   //     this.renderer.setStyle(this.windowElement.nativeElement, 'height', newHeight + 'px');
//   //   } else if (this.resizeHandle === 'right') {
//   //     // Resize from the right
//   //     const newWidth = Math.max(200, this.windowElement.nativeElement.offsetWidth + event.movementX);
//   //     this.renderer.setStyle(this.windowElement.nativeElement, 'width', newWidth + 'px');
//   //   }
//   // }

//   private dragWindow(event: MouseEvent) {
//       const newX = this.initialX - this.offsetX + event.clientX;
//       const newY = this.initialY - this.offsetY + event.clientY;
//       // console.log('==>pos', this.initialX, event.clientX - this.offsetX, this.initialY, event.clientY - this.offsetY);
//       // console.log('newXY', newX, newY, event.movementX, event.movementY, this.initialX, this.initialY, this.offsetX, this.offsetY);
//       this.renderer.setStyle(this.windowEl.nativeElement, 'transform', 'unset');
//       this.renderer.setStyle(this.windowEl.nativeElement, 'left', newX + 'px');
//       this.renderer.setStyle(this.windowEl.nativeElement, 'top', newY + 'px');
//   }

//   open(data: any = null) {
//     this.data = data;
//     this.show();
//   }

//   close() {
//     this.hide();
//   }

//   isVisible = false;
//   show() {
//     // this.renderer.setStyle(this.windowEl.nativeElement, 'left', '50%');
//     // this.renderer.setStyle(this.windowEl.nativeElement, 'top', '50%');
//     // this.renderer.setStyle(this.windowEl.nativeElement, 'transform', 'translate(-50%, -50%)');
//     // const windowRect = this.windowEl.nativeElement.getBoundingClientRect();
//     // console.log('----------->', windowRect);
//     // const left = (window.innerWidth - windowRect.width) / 2;
//     // const top = (window.innerHeight - windowRect.height) / 2;
//     // this.renderer.setStyle(this.windowEl.nativeElement, 'transform', 'unset');
//     // this.renderer.setStyle(this.windowEl.nativeElement, 'left', left + 'px');
//     // this.renderer.setStyle(this.windowEl.nativeElement, 'top', top + 'px');
//     this.renderer.setStyle(this.windowEl.nativeElement, 'display', 'flex');
//     this.isVisible = true;
//     this.onOpen.emit();
//   }

//   hide() {
//     this.renderer.setStyle(this.windowEl.nativeElement, 'display', 'none');
//     this.isVisible = false;
//     this.onClose.emit();
//   }

//   toggle() {
//     ['none', ''].includes(this.windowEl.nativeElement.style.display) ? this.show() : this.hide();
//   }

// }
